import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSite,
  deleteSite,
  fetchGenbaSites,
  fetchSites,
  fetchTrusstorProjects,
  ProviderConnectionError,
} from '@apis/projectMerge';
import type { IGenbaSite, ISite, ITrusstorProject } from '@interfaces/IProjectMerge';
import type { ProviderName } from '@interfaces/IAuth';
import { PORTFOLIO_KEY, PROJECTS_LIST_KEY } from '@constants/queryKeys';
import { CenterBridge } from './components/CenterBridge';
import type { BridgePhase } from './components/CenterBridge';
import { GenbaPane } from './components/GenbaPane';
import type { GenbaStatusFilter, PaneViewState } from './components/GenbaPane';
import { TrusstorPane } from './components/TrusstorPane';
import { ExistingLinks } from './components/ExistingLinks';
import type { IResolvedSite } from './components/ExistingLinks';
import { UnlinkModal } from './components/UnlinkModal';
import { Toasts } from '@components/ui/Toasts/Toasts';
import { useToasts } from '@components/ui/Toasts/useToasts';
import type { ILinkedInfo } from './components/SiteRow';
import styles from './ProjectMergePage.module.css';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

// "In operation" = the Genba `in-operation` grouping (everything but completed /
// canceled), keyed off the stable status code (§6-5), not the localized label.
const IN_OPERATION = new Set(['negotiation', 'contracted', 'before-construction', 'under-construction']);

/** Derive a pane's data-availability state from its query lifecycle. A
    connection failure is its own pane state — prompt to (re)bind the provider,
    distinct from a generic fetch error that offers a retry: `not-connected`
    (never bound) → Connect copy, any other connection state (Genba's expired
    session, Trusstor's failed silent refresh) → Reconnect copy. Both route to
    the same connect form; retrying can't fix either (terminal, see the queries'
    `retry` option). */
function paneViewState(
  query: { error: unknown; isError: boolean; isLoading: boolean },
  rowCount: number,
): PaneViewState {
  if (query.error instanceof ProviderConnectionError) {
    return query.error.state === 'not-connected' ? 'noCreds' : 'reauth';
  }
  if (query.isError) return 'error';
  if (query.isLoading) return 'loading';
  return rowCount === 0 ? 'empty' : 'data';
}

const genbaSitesKey = (keywords: string) => ['projectMerge', 'genbaSites', { keywords }] as const;
const trusstorKey = ['projectMerge', 'trusstorProjects'] as const;
const sitesKey = ['projectMerge', 'sites'] as const;
// A new (or deleted) site must show up app-wide, not just in this page's list.
// The topbar project picker and the portfolio read SEPARATE queries (keyed by
// the shared PROJECTS_LIST_KEY / PORTFOLIO_KEY), so after a create/delete we
// invalidate those too — the picker (mounted in the shell) refetches
// immediately; the portfolio is marked stale for its next visit.

/** Sync / New Site (現場連携) — admin page that mints a D-ORCA-owned site from
    one Genba Plus site + one Trusstor project + a D-ORCA name. The only screen
    that names both upstreams by design. */
export function ProjectMergePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { toasts, push, dismiss } = useToasts();

  /* ---- pane filter / paging state ---------------------------------------- */
  const [genbaSearch, setGenbaSearch] = useState('');
  const [genbaStatus, setGenbaStatus] = useState<GenbaStatusFilter>('in-operation');
  const [trusstorSearch, setTrusstorSearch] = useState('');

  /* ---- selection + bridge ------------------------------------------------ */
  const [genbaSelId, setGenbaSelId] = useState<string | null>(null);
  const [trusstorSelId, setTrusstorSelId] = useState<string | null>(null);
  // The D-ORCA name for the site being minted. Whether the user has typed into
  // it is tracked in a ref (not state) so an untouched field can keep auto-
  // prefilling from the picked Genba/Trusstor name; once edited we leave it
  // alone. It never drives render — only the prefill decision — so a ref avoids
  // a wasted re-render on first keystroke.
  const [siteName, setSiteName] = useState('');
  const nameTouched = useRef(false);
  const [newSiteId, setNewSiteId] = useState<string | null>(null);

  /* ---- your-sites + delete ----------------------------------------------- */
  const [existingOpen, setExistingOpen] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<ISite | null>(null);

  // setTimeout that resets the bridge after the success state has been read —
  // tracked so an unmount mid-window doesn't fire setState on a dead component.
  const successResetTimer = useRef<number | undefined>(undefined);

  /* ---- queries ----------------------------------------------------------- */
  // Keyword search is the server seam; status filter + paging are applied over
  // the returned set client-side (the "active" selector spans two statuses,
  // which the single-status endpoint can't express). Faithful to the design,
  // which presents this list as server-paginated.
  const genbaQuery = useQuery({
    queryKey: genbaSitesKey(genbaSearch.trim().toLowerCase()),
    queryFn: ({ signal }) => fetchGenbaSites({ keywords: genbaSearch.trim() }, signal),
    placeholderData: keepPreviousData,
    // A connection state (not-connected / expired-session) is terminal, not a
    // transient failure — retrying can't fix it, so don't (matches the portfolio
    // ProviderError handling).
    retry: (failureCount, err) =>
      err instanceof ProviderConnectionError ? false : failureCount < 2,
  });

  const trusstorQuery = useQuery({
    queryKey: trusstorKey,
    queryFn: ({ signal }) => fetchTrusstorProjects(signal),
    // Same as Genba: a not-connected / disconnected state is terminal — a retry
    // can't repair it, the user must (re)connect.
    retry: (failureCount, err) =>
      err instanceof ProviderConnectionError ? false : failureCount < 2,
  });

  const sitesQuery = useQuery({
    queryKey: sitesKey,
    queryFn: ({ signal }) => fetchSites(signal),
  });

  /* ---- lookups ----------------------------------------------------------- */
  const genbaById = useMemo(() => {
    const byNumber = new Map<string, IGenbaSite>();
    (genbaQuery.data?.sites ?? []).forEach((site) => byNumber.set(site.number, site));
    return byNumber;
  }, [genbaQuery.data]);

  const trusstorById = useMemo(() => {
    const byId = new Map<string, ITrusstorProject>();
    (trusstorQuery.data ?? []).forEach((project) => byId.set(project.id, project));
    return byId;
  }, [trusstorQuery.data]);

  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);

  // Genba number -> Trusstor partner; Trusstor id -> Genba partner. A site
  // already carrying an upstream id marks that picker row as taken.
  const linkedGenba = useMemo(() => {
    const byNumber: Record<string, ILinkedInfo> = {};
    sites.forEach((site) => {
      const partner = trusstorById.get(site.trusstorProjectId);
      byNumber[site.genbaSiteNumber] = {
        name: partner?.name ?? site.trusstorProjectId,
        code: site.trusstorProjectId,
      };
    });
    return byNumber;
  }, [sites, trusstorById]);

  const linkedTrusstor = useMemo(() => {
    const byId: Record<string, ILinkedInfo> = {};
    sites.forEach((site) => {
      const partner = genbaById.get(site.genbaSiteNumber);
      byId[site.trusstorProjectId] = {
        name: partner?.name ?? site.genbaSiteNumber,
        code: site.genbaSiteNumber,
      };
    });
    return byId;
  }, [sites, genbaById]);

  /* ---- mutations --------------------------------------------------------- */
  const createMutation = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: ({ name, trusstor, genba }: { name: string; trusstor: string; genba: string }) =>
      createSite(name, trusstor, genba),
    onSuccess: (created) => {
      setNewSiteId(created.siteId);
      setExistingOpen(true);
      push({
        kind: 'success',
        title: t('projectMerge.toast.created', { name: created.name }),
        sub: created.siteId,
      });
      void queryClient.invalidateQueries({ queryKey: sitesKey });
      void queryClient.invalidateQueries({ queryKey: PROJECTS_LIST_KEY });
      void queryClient.invalidateQueries({ queryKey: PORTFOLIO_KEY });
      // After the success state has been read, reset the selection + bridge.
      successResetTimer.current = window.setTimeout(() => {
        setGenbaSelId(null);
        setTrusstorSelId(null);
        setSiteName('');
        nameTouched.current = false;
        setNewSiteId(null);
        createMutation.reset();
      }, 1700);
    },
    onError: () => {
      push({
        kind: 'error',
        title: t('projectMerge.toast.createFailed'),
        sub: t('projectMerge.toast.createFailedSub'),
      });
    },
  });

  const deleteMutation = useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (site: ISite) => deleteSite(site.siteId),
    onSuccess: (_void, site) => {
      setDeleteTarget(null);
      push({
        kind: 'success',
        title: t('projectMerge.toast.deleted', { name: site.name }),
        sub: t('projectMerge.toast.deletedSub', { siteId: site.siteId }),
      });
      void queryClient.invalidateQueries({ queryKey: sitesKey });
      void queryClient.invalidateQueries({ queryKey: PROJECTS_LIST_KEY });
      void queryClient.invalidateQueries({ queryKey: PORTFOLIO_KEY });
    },
    onError: () => {
      push({
        kind: 'error',
        title: t('projectMerge.toast.deleteFailed'),
      });
    },
  });

  /* ---- genba: status filter (client) ------------------------------------- */
  const genbaStatusFiltered = useMemo(() => {
    const all = genbaQuery.data?.sites ?? [];
    if (genbaStatus === 'all') return all;
    if (genbaStatus === 'in-operation') return all.filter((site) => IN_OPERATION.has(site.status));
    return all.filter((site) => site.status === genbaStatus);
  }, [genbaQuery.data, genbaStatus]);

  const handleGenbaSearch = useCallback((value: string) => {
    setGenbaSearch(value);
  }, []);
  const handleGenbaStatus = useCallback((value: GenbaStatusFilter) => {
    setGenbaStatus(value);
  }, []);

  /* ---- trusstor: client-side filter -------------------------------------- */
  const trusstorRows = useMemo(() => {
    const all = trusstorQuery.data ?? [];
    const query = trusstorSearch.trim().toLowerCase();
    if (!query) return all;
    return all.filter((project) =>
      [project.name, project.id, project.city, project.country]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [trusstorQuery.data, trusstorSearch]);

  /* ---- pane view states (derived from query lifecycle) ------------------- */
  const genbaViewState = paneViewState(genbaQuery, genbaStatusFiltered.length);
  const trusstorViewState = paneViewState(trusstorQuery, trusstorRows.length);

  /* ---- bridge phase (mapped from the create mutation) -------------------- */
  const phase: BridgePhase = createMutation.isPending
    ? 'linking'
    : createMutation.isError
      ? 'conflict'
      : createMutation.isSuccess
        ? 'success'
        : 'idle';

  const genbaSel = genbaSelId ? (genbaById.get(genbaSelId) ?? null) : null;
  const trusstorSel = trusstorSelId ? (trusstorById.get(trusstorSelId) ?? null) : null;

  // Mirror of the live Genba selection, read inside `selectTrusstor` so that
  // handler can stay referentially stable across Genba picks — depending on
  // `genbaSelId` directly would re-create it on every left-pane click, churning
  // TrusstorPane.onSelect (and every memoized row beneath it). Synced in an
  // effect (writing a ref during render is disallowed).
  const genbaSelIdRef = useRef<string | null>(genbaSelId);
  useEffect(() => {
    genbaSelIdRef.current = genbaSelId;
  }, [genbaSelId]);

  // An untouched name field auto-fills from the most recent pick (Genba name
  // first, since it's the JP primary; else the Trusstor name) as an editable
  // default. Once the user edits the field, we stop overriding it.
  const prefillName = useCallback((candidate: string | undefined) => {
    if (!candidate) return;
    if (!nameTouched.current) setSiteName(candidate);
  }, []);

  /* ---- handlers ---------------------------------------------------------- */
  const selectGenba = useCallback(
    (genbaNumber: string) => {
      setGenbaSelId((prev) => {
        const next = prev === genbaNumber ? null : genbaNumber;
        if (next) prefillName(genbaById.get(next)?.name);
        return next;
      });
      createMutation.reset();
    },
    [createMutation, genbaById, prefillName],
  );
  const selectTrusstor = useCallback(
    (id: string) => {
      setTrusstorSelId((prev) => {
        const next = prev === id ? null : id;
        // Only fall back to the Trusstor name when no Genba name is staged.
        if (next && genbaSelIdRef.current === null) prefillName(trusstorById.get(next)?.name);
        return next;
      });
      createMutation.reset();
    },
    [createMutation, trusstorById, prefillName],
  );
  const onNameChange = useCallback((value: string) => {
    setSiteName(value);
    nameTouched.current = true;
  }, []);
  const clearSelection = useCallback(() => {
    setGenbaSelId(null);
    setTrusstorSelId(null);
    setSiteName('');
    nameTouched.current = false;
    createMutation.reset();
  }, [createMutation]);

  const trimmedName = siteName.trim();
  const doCreate = useCallback(() => {
    if (!genbaSelId || !trusstorSelId || !trimmedName) return;
    createMutation.mutate({ name: trimmedName, genba: genbaSelId, trusstor: trusstorSelId });
  }, [genbaSelId, trusstorSelId, trimmedName, createMutation]);

  // Conflict means another admin claimed one side first — reload the sites
  // state, clear the (now invalid) selection, and drop the bridge to idle.
  const retryConflict = useCallback(() => {
    createMutation.reset();
    setGenbaSelId(null);
    setTrusstorSelId(null);
    void queryClient.invalidateQueries({ queryKey: sitesKey });
    push({
      kind: 'success',
      title: t('projectMerge.toast.reloaded'),
      sub: t('projectMerge.toast.reloadedSub'),
    });
  }, [createMutation, queryClient, push, t]);

  // The success-reset setTimeout outlives a quick unmount (navigate away in the
  // ~1.7s success window); clear it so it never fires setState on a dead tree.
  useEffect(() => () => window.clearTimeout(successResetTimer.current), []);

  const retryGenba = useCallback(() => {
    void genbaQuery.refetch();
  }, [genbaQuery]);
  const retryTrusstor = useCallback(() => {
    void trusstorQuery.refetch();
  }, [trusstorQuery]);
  // (Re)connect a provider — send the admin to its provider-connect form
  // (Genba's also asks for rootUrl + prime-user login), carrying a `redirect`
  // back to this page so a successful (re)connect returns here (not the
  // dashboard) and the pane's query refetches with a live session. Per-provider
  // wrappers keep the panes' `onConnect` props referentially stable.
  const connectUpstream = useCallback(
    (provider: ProviderName) => {
      const back = encodeURIComponent(location.pathname + location.search);
      navigate(`/connect/${provider}?redirect=${back}`);
    },
    [navigate, location],
  );
  const connectGenba = useCallback(() => connectUpstream('genba'), [connectUpstream]);
  const connectTrusstor = useCallback(() => connectUpstream('trusstor'), [connectUpstream]);

  /* ---- resolved your-sites rows ------------------------------------------ */
  const resolvedSites: IResolvedSite[] = useMemo(
    () =>
      sites.map((site) => ({
        site,
        genbaName: genbaById.get(site.genbaSiteNumber)?.name ?? site.genbaSiteNumber,
        trusstorName: trusstorById.get(site.trusstorProjectId)?.name ?? site.trusstorProjectId,
      })),
    [sites, genbaById, trusstorById],
  );

  const deleteGenbaName = deleteTarget
    ? (genbaById.get(deleteTarget.genbaSiteNumber)?.name ?? deleteTarget.genbaSiteNumber)
    : '';
  const deleteTrusstorName = deleteTarget
    ? (trusstorById.get(deleteTarget.trusstorProjectId)?.name ?? deleteTarget.trusstorProjectId)
    : '';

  return (
    <>
      <div className={styles.shell}>
        <div className={styles.board}>
          <GenbaPane
            viewState={genbaViewState}
            search={genbaSearch}
            onSearchChange={handleGenbaSearch}
            statusFilter={genbaStatus}
            onStatusFilterChange={handleGenbaStatus}
            rows={genbaStatusFiltered}
            total={genbaStatusFiltered.length}
            selected={genbaSelId}
            onSelect={selectGenba}
            linkedMap={linkedGenba}
            onRetry={retryGenba}
            onConnect={connectGenba}
          />

          <CenterBridge
            genbaSel={genbaSel}
            trusstorSel={trusstorSel}
            phase={phase}
            newSiteId={newSiteId}
            name={siteName}
            onNameChange={onNameChange}
            onCreate={doCreate}
            onClear={clearSelection}
            onRetry={retryConflict}
          />

          <TrusstorPane
            viewState={trusstorViewState}
            search={trusstorSearch}
            onSearchChange={setTrusstorSearch}
            rows={trusstorRows}
            total={(trusstorQuery.data ?? []).length}
            selected={trusstorSelId}
            onSelect={selectTrusstor}
            linkedMap={linkedTrusstor}
            onRetry={retryTrusstor}
            onConnect={connectTrusstor}
          />
        </div>

        <ExistingLinks
          sites={resolvedSites}
          open={existingOpen}
          onToggle={() => setExistingOpen((prev) => !prev)}
          onDelete={setDeleteTarget}
        />
      </div>

      <UnlinkModal
        site={deleteTarget}
        genbaName={deleteGenbaName}
        trusstorName={deleteTrusstorName}
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={(site) => deleteMutation.mutate(site)}
      />

      <Toasts toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
